import React, { ChangeEvent, useEffect, useState } from 'react'
import { FormFieldType, formFieldTypes } from '../../modules/forms/enums/FormFieldType'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import styles from './EditorField.module.css'
import { isSpecialType } from '../../common/utils/enums'
import { FormFieldDTO } from '../../modules/forms/models/FormDTO'
import { TrashIcon } from '../../components/icons/TrashIcon'

// Mismo aspecto de campo que el editor: pildora gris sin borde.
const CAMPO_RELLENO = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '999px',
    backgroundColor: 'var(--gray-soft, #e8e7ec)',
    fontSize: '14px',
  },
  '& fieldset': { border: 'none' },
}

const ETIQUETA = { fontSize: '14px', color: 'var(--gray-medium)', width: 70, flexShrink: 0 }

type FormEditorFieldProps = {
  handleChange: (item: FormFieldDTO) => void
  handleRemove: (id: string) => void
  formField: FormFieldDTO
}

const fieldTypes = formFieldTypes()

export const EditorFieldsItem: React.FC<FormEditorFieldProps> = (props) => {
  const { t } = useTranslation()
  const [formField, setFormField] = useState<FormFieldDTO>(props.formField)

  const [currentOption, setCurrentOption] = useState<string>('')

  // TODO implement in the future
  // TODO validate if there are optionValues, correctValues must be subset of those
  //  const [correctValues, setCorrectValues] = useState([])

  const handleType = (e: SelectChangeEvent<FormFieldType>, name: string) =>
    setFormField(Object.assign({ ...formField }, { [name]: +(e.target.value as number) }))

  const handleOrder = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, name: string) => {
    const newOrder = parseInt(e.target.value)

    if (newOrder < 1) {
      return
    }

    setFormField(Object.assign({ ...formField }, { [name]: newOrder }))
  }

  const handleInput = (e: ChangeEvent<HTMLInputElement>) =>
    setFormField(Object.assign({ ...formField }, { [e.target.name]: e.target.value }))

  const handleRequired = (_: ChangeEvent<HTMLInputElement>) =>
    setFormField(Object.assign({ ...formField }, { required: !formField.required }))

  const handleOption = (option: string) =>
    setFormField(
      Object.assign(
        { ...formField },
        {
          optionValues: formField.optionValues.filter((o) => o !== option),
        }
      )
    )

  useEffect(() => {
    props.handleChange(formField)
  }, [formField])

  const enterHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (
        currentOption.length > 0 &&
        !formField.optionValues.includes(currentOption.toLowerCase())
      ) {
        setFormField(
          Object.assign(
            { ...formField },
            { optionValues: [...formField.optionValues, currentOption] }
          )
        )
      }
      setCurrentOption('')
    }
  }

  const deleteFormField = () => props.handleRemove(formField.id)

  return (
    <Box className={styles.formEditorField}>
      <Box mb={2} display="flex" alignItems="center" gap={2}>
        <Typography sx={ETIQUETA}>{t('order')}</Typography>
        <TextField
          key="order"
          size="small"
          inputProps={{ min: '1', max: '100', type: 'number' }}
          type="number"
          name="order"
          value={formField.order}
          onChange={(e) => handleOrder(e, 'order')}
          onWheel={(e) => e.preventDefault()}
          onFocus={(e) =>
            e.target.addEventListener('wheel', (evt) => evt.preventDefault(), { passive: false })
          }
          sx={{ ...CAMPO_RELLENO, width: 90 }}
        />
        <Typography sx={{ ...ETIQUETA, width: 'auto' }}>{t('title')}</Typography>
        <TextField
          key="title"
          size="small"
          name="title"
          value={formField.title}
          onChange={handleInput}
          placeholder={t('insertQuestionTitle')}
          required
          sx={{ ...CAMPO_RELLENO, flex: 1 }}
        />
      </Box>
      <Box display="flex" alignItems="center" gap={2}>
        <Typography sx={ETIQUETA}>{t('type')}</Typography>
        <Select
          key={'type'}
          id={'type'}
          size="small"
          value={formField.type}
          onChange={(e) => handleType(e, 'type')}
          required
          sx={{
            borderRadius: '999px',
            fontSize: '14px',
            backgroundColor: 'var(--gray-soft, #e8e7ec)',
            minWidth: 150,
            '& fieldset': { border: 'none' },
          }}
        >
          {Object.keys(fieldTypes).map((k, i) => (
            <MenuItem key={'form-editor-field_menuItem_' + i} value={k}>
              {fieldTypes[k as unknown as FormFieldType]}
            </MenuItem>
          ))}
        </Select>
        {isSpecialType(formField.type) && (
          <Box className={styles.optionBox}>
            <TextField
              className={styles.input}
              key="currentOption"
              variant="outlined"
              placeholder={t('optionValues') + ' ⏎'}
              name="currentOption"
              value={currentOption}
              onChange={(e) => setCurrentOption(e.target.value)}
              onKeyPress={enterHandler}
            />
            {Array.isArray(formField.optionValues) ? (
              <ul className={styles.list}>
                {formField.optionValues
                  .filter((o) => o !== '')
                  .map((o, i) => (
                    <li key={i} className={styles.listItem}>
                      <Chip
                        label={o}
                        onDelete={() => handleOption(o)}
                        onClick={() => handleOption(o)}
                      />
                    </li>
                  ))}
              </ul>
            ) : (
              <></>
            )}
          </Box>
        )}
      </Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
        <FormControlLabel
          label={<Typography sx={{ fontSize: '14px' }}>{t('compulsory')}</Typography>}
          labelPlacement={'start'}
          sx={{ ml: 0, gap: 1 }}
          control={
            <Checkbox
              key="required"
              name="required"
              size="small"
              value={formField.required}
              checked={formField.required}
              onChange={handleRequired}
            />
          }
        />
        {/* Pildora rosa con el texto y el icono en rojo, como el resto de
            acciones destructivas de la app. */}
        <Button
          type="button"
          onClick={deleteFormField}
          startIcon={<TrashIcon sx={{ fontSize: 16 }} />}
          sx={{
            textTransform: 'none',
            borderRadius: '999px',
            px: 2,
            fontSize: '13px',
            backgroundColor: 'rgba(225, 76, 84, 0.16)',
            color: 'var(--destructive, #e14c54)',
            '&:hover': { backgroundColor: 'rgba(225, 76, 84, 0.26)' },
          }}
        >
          {t('delete')}
        </Button>
      </Box>
    </Box>
  )
}
